// External dependencies
import { useTranslation } from 'react-i18next';
import AsyncSelect from 'react-select/async';
import type { OnChangeValue } from 'react-select';
import React, { useState, useEffect } from 'react';
import { Alert } from '@trustroots/ui/Alert';

// Internal dependencies
import { useLanguagesQuery, type LanguageOption } from '../api/languages.api';
import { normaliseForSearch } from '../utils/locales';

const INPUT_MIN_LENGTH = 2;

function filterLanguagesByLabel(
  languages: LanguageOption[],
  inputValue: string,
): LanguageOption[] {
  const query = normaliseForSearch(inputValue);
  return languages.filter(language =>
    normaliseForSearch(language.label).includes(query),
  );
}

type LanguageSelectProps = {
  excludeDeprecated?: boolean;
  preSelectedLanguages?: string[];
  placeholder?: string;
  onChangeLanguages?: (languages: string[]) => void;
  [key: string]: unknown;
};

export default function LanguageSelect({
  excludeDeprecated = false,
  preSelectedLanguages = [],
  placeholder,
  onChangeLanguages,
  ...props
}: LanguageSelectProps) {
  const { t } = useTranslation('core');
  const { data, isLoading, isError } = useLanguagesQuery({
    format: 'array',
  });
  const [selected, setSelected] = useState<LanguageOption[]>([]);

  useEffect(() => {
    if (data && preSelectedLanguages?.length) {
      const prefill = data.filter(({ value }) =>
        preSelectedLanguages.includes(value),
      );
      setSelected(prefill);
    }
  }, [data]);

  const filteredLanguages = (inputValue: string): Promise<LanguageOption[]> =>
    new Promise(resolve => {
      if (!inputValue || inputValue.length < INPUT_MIN_LENGTH) {
        return resolve([]);
      }

      const languages = data || [];
      const options = excludeDeprecated
        ? languages.filter(language => !language.deprecated)
        : languages;
      resolve(filterLanguagesByLabel(options, inputValue));
    });

  const onChange = (selectedOptions: OnChangeValue<LanguageOption, true>) => {
    if (onChangeLanguages) {
      const languageCodes =
        selectedOptions && selectedOptions.length
          ? selectedOptions.map(({ value }) => value)
          : [];
      onChangeLanguages(languageCodes);
    }
    setSelected(selectedOptions ? [...selectedOptions] : []);
  };

  return (
    <>
      <AsyncSelect
        isDisabled={!data || isError}
        isLoading={isLoading}
        isMulti
        value={selected}
        loadingMessage={() => t('Loading…')}
        noOptionsMessage={({ inputValue }) => {
          return inputValue?.length >= INPUT_MIN_LENGTH
            ? t('No languages found; try typing something else.')
            : t('Start typing a language…');
        }}
        placeholder={placeholder || t('Select…')}
        onChange={onChange}
        loadOptions={filteredLanguages}
        {...props}
      />
      {isError && (
        <Alert>
          {t(
            'Snap! Something went wrong. If this keeps happening, please contact us.',
          )}
        </Alert>
      )}
    </>
  );
}
